/**
 * Dual-handle price range slider.
 *
 * Drives Horizon's existing price facet inputs (ref="minInput" / ref="maxInput") rather than
 * replacing them: dragging a handle writes into those inputs, and the range input's own `change`
 * event bubbles to <price-facet-component on:change="/updatePriceFilterAndResults">, so the
 * native filtering / URL / section-rendering logic runs unchanged.
 *
 * Registered as a custom element so it re-initialises when the facets section is re-rendered.
 * Listeners are delegated (component-level for the handles, document-level for the number
 * fields) so they survive the section morphing its children after a filter change.
 */
if (!customElements.get('price-slider-component')) {
  class PriceSliderComponent extends HTMLElement {
    connectedCallback() {
      if (!this.bound) {
        this.bound = true;
        this.addEventListener('input', (event) => {
          const target = event.target;
          if (target instanceof HTMLInputElement && target.type === 'range') {
            this.onRangeInput(target.dataset.role);
          }
        });
      }
      // Re-resolve refs and repaint on every connect: the section morphs on filter changes.
      this.syncFromInputs();
    }

    get minRange() {
      return this.querySelector('[data-role="min"]');
    }
    get maxRange() {
      return this.querySelector('[data-role="max"]');
    }
    get fill() {
      return this.querySelector('.price-slider__fill');
    }
    get facet() {
      return this.closest('price-facet-component');
    }
    get minInput() {
      return this.facet?.querySelector('[ref="minInput"]');
    }
    get maxInput() {
      return this.facet?.querySelector('[ref="maxInput"]');
    }
    get max() {
      return Number(this.maxRange?.max) || 1;
    }

    /** Parse a formatted money value ("4,744.00") into a number. */
    parse(value) {
      const parsed = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
      return Number.isNaN(parsed) ? null : parsed;
    }

    /** Dragging a handle: keep the handles ordered, mirror into the facet's number fields. */
    onRangeInput(role) {
      const { minRange, maxRange } = this;
      if (!minRange || !maxRange) return;

      let low = Number(minRange.value);
      let high = Number(maxRange.value);

      if (low > high) {
        if (role === 'min') {
          low = high;
          minRange.value = String(low);
        } else {
          high = low;
          maxRange.value = String(high);
        }
      }

      // Leave a field empty at its bound so no redundant filter param is added.
      if (this.minInput) this.minInput.value = low > 0 ? String(low) : '';
      if (this.maxInput) this.maxInput.value = high < this.max ? String(high) : '';

      this.paint();
    }

    /** Typing in the number fields moves the handles. */
    syncFromInputs() {
      const { minRange, maxRange } = this;
      if (!minRange || !maxRange) return;

      const clamp = (value) => Math.min(Math.max(value, 0), this.max);
      minRange.value = String(clamp(this.parse(this.minInput?.value) ?? 0));
      maxRange.value = String(clamp(this.parse(this.maxInput?.value) ?? this.max));
      this.paint();
    }

    paint() {
      const { minRange, maxRange, fill } = this;
      if (!minRange || !maxRange) return;

      const low = Number(minRange.value);
      const high = Number(maxRange.value);

      if (fill) {
        fill.style.left = `${(low / this.max) * 100}%`;
        fill.style.right = `${100 - (high / this.max) * 100}%`;
      }

      // Keep the min handle grabbable when both sit at the far right.
      minRange.style.zIndex = low >= this.max ? '5' : '';
    }
  }

  customElements.define('price-slider-component', PriceSliderComponent);

  // The number fields live outside the component, so delegate at the document level.
  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const ref = target.getAttribute('ref');
    if (ref !== 'minInput' && ref !== 'maxInput') return;
    target.closest('price-facet-component')?.querySelector('price-slider-component')?.syncFromInputs();
  });
}
